import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useResource, formatDate } from "../lib/hooks";
import { EntityForm, Field } from "../components/EntityForm";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ANNOUNCEMENT_CHANNELS } from "../lib/formOptions";
import type { Announcement } from "../lib/types";

const CHANNEL_OPTIONS = ANNOUNCEMENT_CHANNELS.map((c) => ({ value: c.value, label: c.label }));

const fields: Field[] = [
    { name: "channel", label: "Channel", kind: "select", required: true, options: CHANNEL_OPTIONS, half: true },
    { name: "published_at", label: "Published", kind: "datetime", half: true,
      help: "Leave blank to publish now." },
    { name: "title", label: "Title", kind: "text", required: true },
    { name: "body", label: "Body", kind: "textarea", required: true },
    { name: "link_url", label: "Link (optional)", kind: "text", placeholder: "https://…" },
    { name: "expires_at", label: "Expires", kind: "date",
      help: "Leave blank for a post that never expires." },
];

type Dialog = { kind: "create" } | { kind: "edit"; item: Announcement } | { kind: "delete"; item: Announcement } | null;

export const Updates = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";

    const items = useResource<Announcement[]>("/announcements");
    const [channel, setChannel] = useState<Announcement["channel"]>("organisation");
    const [dialog, setDialog] = useState<Dialog>(null);

    const refresh = () => { items.reload(); setDialog(null); };

    const active = ANNOUNCEMENT_CHANNELS.find((c) => c.value === channel)!;
    const shown = (items.data ?? [])
        .filter((item) => item.channel === channel)
        .sort((a, b) => b.published_at.localeCompare(a.published_at));

    if (items.error) return <div className="error-banner">{items.error}</div>;

    return (
        <div>
            {dialog?.kind === "create" && (
                <EntityForm title="Post an update" path="/announcements" fields={fields}
                    fixed={{ channel }}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm title="Edit update" path="/announcements" fields={fields}
                    initial={dialog.item}
                    onClose={() => setDialog(null)} onSaved={refresh} />
            )}
            {dialog?.kind === "delete" && (
                <ConfirmDialog
                    title="Remove this update?"
                    tone="danger"
                    message={dialog.item.title}
                    confirmLabel="Remove"
                    request={{ method: "delete", path: `/announcements/${dialog.item.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh} />
            )}

            <div className="toolbar">
                <div className="tabs" style={{ marginBottom: 0, flexWrap: "wrap" }}>
                    {ANNOUNCEMENT_CHANNELS.map((c) => (
                        <button key={c.value} className={channel === c.value ? "active" : ""}
                            onClick={() => setChannel(c.value)}>
                            {c.label}
                        </button>
                    ))}
                </div>
                <div className="spacer" />
                {canManage && (
                    <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Post update</button>
                )}
            </div>

            <div className="card">
                <div className="card-body">
                    {items.loading ? (
                        <div className="empty">Loading…</div>
                    ) : shown.length === 0 ? (
                        <div className="empty">Nothing posted in {active.label} yet.</div>
                    ) : shown.map((item) => (
                        <article key={item.id} className="feed-item">
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "flex-start" }}>
                                <h4>{item.title}</h4>
                                {canManage && (
                                    <div className="row-actions" style={{ flexShrink: 0 }}>
                                        <button className="icon-btn" onClick={() => setDialog({ kind: "edit", item })}>Edit</button>
                                        <button className="icon-btn danger" onClick={() => setDialog({ kind: "delete", item })}>Remove</button>
                                    </div>
                                )}
                            </div>
                            <p>{item.body}</p>
                            <time dateTime={item.published_at}>{formatDate(item.published_at)}</time>
                            {item.link_url && (
                                <>
                                    {" · "}
                                    <a href={item.link_url} target="_blank" rel="noreferrer" style={{ fontSize: "0.76rem" }}>
                                        Read at source
                                    </a>
                                </>
                            )}
                        </article>
                    ))}
                    {active.sourceUrl && (
                        <p style={{ marginTop: "0.6rem", fontSize: "0.76rem" }}>
                            <a href={active.sourceUrl} target="_blank" rel="noreferrer">More from {active.sourceLabel} ↗</a>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};
